<template>
  <div>
    <v-row no-gutters>
      <v-col cols="12">
        <v-file-input
          ref="picker"
          v-model="cover_image"
          :label="computedLabel"
          clearable
          :rules="isEdit ? [] : [rules.required]"
          outlined
          v-bind="$attrs"
          :hint="hint"
          :persistent-hint="!!hint"
          accept="image/*"
          prepend-inner-icon="mdi-paperclip"
          prepend-icon=""
          :error-messages="errors.cover_image"
          @change="loadIntoCropper"
        ></v-file-input>
      </v-col>
    </v-row>
    <v-dialog
      v-model="cropModal"
      content-class="overflow-hidden height"
      fullscreen
      persistent
    >
      <v-card
        tile
        class="image-cropper image-cropper__cropper-wrapper"
        color="black"
      >
        <cropper
          ref="cropper"
          :stencil-props="{
            aspectRatio: ratio,
          }"
          class="image-cropper__cropper"
          image-restriction="fit-area"
          check-orientation
          :src="cropper_cover_image.src"
          :default-size="defaultSize"
          @change="onChange"
        />
        <div class="image-cropper__control-buttons">
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn class="mr-1" dark icon v-on="on" @click="zoom(2)">
                <v-icon>mdi-magnify-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('cropper.zoom_in') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn class="mr-1" dark icon v-on="on" @click="zoom(0.5)">
                <v-icon>mdi-magnify-minus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('cropper.zoom_out') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn
                class="mr-1"
                dark
                icon
                v-on="on"
                @click="flip(true, false)"
              >
                <v-icon>mdi-flip-horizontal</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('cropper.flip_horizontal') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn
                class="mr-1"
                dark
                icon
                v-on="on"
                @click="flip(false, true)"
              >
                <v-icon>mdi-flip-vertical</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('cropper.flip_vertical') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn class="mr-1" dark icon v-on="on" @click="rotate(90)">
                <v-icon>mdi-rotate-right</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('cropper.rotate_right') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn class="mr-1" dark icon v-on="on" @click="rotate(-90)">
                <v-icon>mdi-rotate-left</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('cropper.rotate_left') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn class="mr-1" dark icon v-on="on" @click="crop">
                <v-icon>mdi-content-save</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('buttons.save') }}</span>
          </v-tooltip>
        </div>
        <div class="image-cropper__reset-button">
          <v-btn dark icon @click="reset">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import 'vue-advanced-cropper/dist/theme.classic.css'
import { mapMutations, mapState } from 'vuex'

export default {
  name: 'ImageCropper',
  components: { Cropper },
  props: {
    label: {
      default: '',
      type: String,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    ratio: {
      type: Number,
      default: 3 / 2,
    },
    hint: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      rules: {
        required: (value) =>
          (value && Boolean(value)) || value === 0 || 'Câmp Obligatoriu',
      },
      cropper_cover_image: {},
      cropModal: false,
      cover_image: null,
      defaultLabel: this.$t('fields.cover_image'),
    }
  },
  computed: {
    ...mapState('errors', ['errors']),
    computedLabel() {
      return this.label ? this.label : this.defaultLabel
    },
  },
  methods: {
    ...mapMutations('errors', ['clearError']),
    imageCrop() {},
    flip(x, y) {
      this.$refs.cropper.flip(x, y)
    },
    rotate(angle) {
      this.$refs.cropper.rotate(angle)
    },
    zoom(factor) {
      this.$refs.cropper.zoom(factor)
    },
    loadIntoCropper(event) {
      this.clearError('cover_image')
      if (event) {
        if (this.cropper_cover_image.src) {
          URL.revokeObjectURL(this.cropper_cover_image.src)
        }

        const blob = URL.createObjectURL(event)
        const reader = new FileReader()

        reader.onload = (e) => {
          this.cropper_cover_image = {
            src: blob,
            type: this.getMimeType(e.target.result, event.type),
          }
        }
        reader.readAsArrayBuffer(event)
        this.cropModal = true
      }
    },
    reset() {
      this.cropper_cover_image = {
        src: null,
        type: null,
      }
      this.cover_image = null
      this.cropModal = false
    },
    destroyed() {
      if (this.cover_image.src) {
        URL.revokeObjectURL(this.cover_image.src)
      }
    },
    crop() {
      const { canvas } = this.$refs.cropper.getResult()
      canvas.toBlob((blob) => {
        this.$emit('image-ready', blob)
        this.cropModal = false
      }, this.cropper_cover_image.type)
    },
    getMimeType(file, fallback = null) {
      const byteArray = new Uint8Array(file).subarray(0, 4)
      let header = ''
      for (let i = 0; i < byteArray.length; i++) {
        header += byteArray[i].toString(16)
      }
      switch (header) {
        case '89504e47':
          return 'image/png'
        case '47494638':
          return 'image/gif'
        case 'ffd8ffe0':
        case 'ffd8ffe1':
        case 'ffd8ffe2':
        case 'ffd8ffe3':
        case 'ffd8ffe8':
          return 'image/jpeg'
        default:
          return fallback
      }
    },
    onChange({ coordinates, image }) {
      this.result = {
        coordinates,
        image,
      }
    },
    defaultSize({ imageSize, visibleArea }) {
      return {
        width: (visibleArea || imageSize).width,
        height: (visibleArea || imageSize).height,
      }
    },
  },
}
</script>

<style lang="scss">
.image-cropper__cropper {
  max-height: 100vh !important;
}
.image-cropper {
  &__cropper .vue-advanced-cropper {
    height: 100vh !important;
  }

  user-select: none;
  overflow: hidden !important;
  &__cropper-wrapper {
    position: relative;
  }
  &__control-buttons {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }
  &__reset-button {
    position: fixed;
    right: 25px;
    top: 25px;
  }
}
</style>
